import { useCallback, useRef, useState } from "react";

// Reusable horizontal swipe-to-act gesture, factored from the Session-tab plan
// rows (SessionPlanDisplay) so the programme timeline reuses the same feel
// (#59). Touch/pointer only; the caller renders the reveal backgrounds and
// applies `translateX(offset)`.
//
//   const { offset, dragging, handlers } = useSwipeActions({
//     onSwipeLeft, onSwipeRight,
//   });
//   <div {...handlers} style={{ transform: `translateX(${offset}px)` }} />
//
// Negative offset = swiped left (reveals the left/destructive action);
// positive = swiped right (reveals the right/edit action). Crossing THRESHOLD
// on release fires the matching callback.

const THRESHOLD = 80;
const MAX_REVEAL = 120;
const DIRECTION_LOCK = 8; // px of vertical drift that cancels a swipe (= scroll)

export function useSwipeActions({
  onSwipeLeft,
  onSwipeRight,
  enabled = true,
  threshold = THRESHOLD,
  maxReveal = MAX_REVEAL,
} = {}) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const state = useRef({ active: false, startX: 0, startY: 0, pointerId: null });

  const active = enabled && (Boolean(onSwipeLeft) || Boolean(onSwipeRight));

  const onPointerDown = useCallback(
    (e) => {
      if (!active) return;
      // Mouse users get the card's overflow menu instead; keep swipe to
      // touch + pen so a click-drag never hijacks a normal click.
      if (e.pointerType === "mouse") return;
      state.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        pointerId: e.pointerId,
      };
      // Capture the pointer so move/up keep arriving here even as the finger
      // drifts off the row (without this, real touch swipes silently drop).
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* Safari can throw on an already-captured pointer — safe to ignore. */
      }
      setDragging(true);
    },
    [active],
  );

  const reset = useCallback(() => {
    state.current.active = false;
    setDragging(false);
    setOffset(0);
  }, []);

  const onPointerMove = useCallback(
    (e) => {
      const s = state.current;
      if (!s.active || s.pointerId !== e.pointerId) return;
      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;
      // Vertical intent → it's a scroll, not a swipe: bail and let it through.
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > DIRECTION_LOCK) {
        reset();
        return;
      }
      if (dx < 0 && onSwipeLeft) {
        setOffset(Math.max(dx, -maxReveal));
      } else if (dx > 0 && onSwipeRight) {
        setOffset(Math.min(dx, maxReveal));
      } else {
        setOffset(0);
      }
    },
    [onSwipeLeft, onSwipeRight, maxReveal, reset],
  );

  const onPointerUp = useCallback(
    (e) => {
      const s = state.current;
      if (!s.active || s.pointerId !== e.pointerId) return;
      const dx = e.clientX - s.startX;
      reset();
      if (dx <= -threshold && onSwipeLeft) onSwipeLeft();
      else if (dx >= threshold && onSwipeRight) onSwipeRight();
    },
    [onSwipeLeft, onSwipeRight, threshold, reset],
  );

  return {
    offset,
    dragging,
    maxReveal,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: reset,
    },
  };
}

export default useSwipeActions;
